import { useEffect, useRef, useState } from 'react';
import { api, type Look, ApiError, type Brand, type Card, type Channel, type ChannelInput, type ChannelRig } from '../lib/api';

/**
 * Channels: finished videos pushed to YouTube, Facebook or any RTMP ingest as
 * if live. Looped so a crowd can arrive at any moment — a concert, a game, a
 * parade — or once for a webinar. The stream key is typed here, sent once,
 * and never shown again: the API keeps it in Secret Manager, not the database.
 */
const TONE: Record<Channel['status'], string> = {
  off: 'var(--ink-3)', starting: 'var(--blue)', live: 'var(--red)', stopping: 'var(--ink-2)', failed: 'var(--red)',
};
/** A timestamp as the datetime-local input wants it, in the viewer's zone. */
function toLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`;
}
function fromLocal(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

function hms(s: number): string {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  const two = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}
function ago(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 90 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
}

const PLATFORM_LABEL = { youtube: 'YouTube Live', facebook: 'Facebook Live', rtmp: 'Custom RTMP' } as const;

export function Channels({ brand }: { brand: Brand | null }) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<ChannelInput> & { id?: string } | null>(null);
  const [keyFor, setKeyFor] = useState<string | null>(null);
  const [streamKey, setStreamKey] = useState('');
  const [alsoFor, setAlsoFor] = useState<string | null>(null);   // P7: adding another destination
  const [alsoPlatform, setAlsoPlatform] = useState<'youtube' | 'facebook' | 'rtmp'>('facebook');
  const [alsoIngest, setAlsoIngest] = useState('');
  const [alsoKey, setAlsoKey] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useRef(false);

  async function load() {
    try {
      const [chs, cs] = await Promise.all([api.channels(), api.cards()]);
      setChannels(chs);
      setCards(cs.cards);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }
  const [looks, setLooks] = useState<Look[]>([]);
  useEffect(() => { api.labLooks(brand?.id ?? null).then((ls) => setLooks(ls.filter((l) => l.status === 'ready' && l.assets.overlay))).catch(() => setLooks([])); }, [brand?.id]);
  useEffect(() => {
    live.current = true;
    void load();
    const t = setInterval(() => { if (live.current) void load(); }, 10_000);
    return () => { live.current = false; clearInterval(t); };
  }, []);

  const finished = cards.filter((c) => !!c.master_job_id || c.stage === 'review' || c.stage === 'scheduled' || c.stage === 'published');
  const titleOf = (id: string) => cards.find((c) => c.id === id)?.title ?? 'a video that is gone';

  async function save() {
    if (!editing?.name?.trim() || !editing.cardIds?.length) { setError('a channel needs a name and at least one video'); return; }
    setBusy(true);
    try {
      const input: ChannelInput = {
        name: editing.name.trim(), brandId: brand?.id ?? null, cardIds: editing.cardIds,
        mode: editing.mode ?? 'loop', platform: editing.platform ?? 'youtube',
        ...(editing.ingestUrl?.trim() ? { ingestUrl: editing.ingestUrl.trim() } : {}),
        rig: editing.rig ?? {},
      };
      if (editing.id) await api.updateChannel(editing.id, input); else await api.createChannel(input);
      setEditing(null);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function start(id: string) {
    if (streamKey.trim().length < 8) { setError('paste the stream key from the platform\'s Go live page'); return; }
    setBusy(true);
    try {
      await api.startChannel(id, streamKey.trim());
      setStreamKey('');
      setKeyFor(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally { setBusy(false); }
  }

  async function stop(id: string) {
    setBusy(true);
    try { await api.stopChannel(id); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  const draft = editing;
  return (
    <section className="panel">
      <div className="vhead" style={{ display: 'flex', alignItems: 'center', gap: 12 }} data-move="channel-failed">
        <h2 style={{ margin: 0 }}>Channels</h2>
        <span className="sub-dim">finished videos, pushed live to YouTube, Facebook or any RTMP ingest — looped for a crowd, or once for a webinar</span>
        <span style={{ flex: 1 }} />
        <button className="btn" disabled={busy || !!draft} onClick={() => setEditing({ mode: 'loop', platform: 'youtube', cardIds: [], rig: { badge: 'LIVE', mark: 'brand' } })}>New channel</button>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {draft && (
        <div className="notice" style={{ display: 'grid', gap: 10, marginBlock: 12 }}>
          <label>Name <input value={draft.name ?? ''} onChange={(e) => setEditing({ ...draft, name: e.target.value })} placeholder="Garden season, all day" /></label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label>Platform{' '}
              <select value={draft.platform ?? 'youtube'} onChange={(e) => setEditing({ ...draft, platform: e.target.value as ChannelInput['platform'] })}>
                <option value="youtube">YouTube Live</option>
                <option value="facebook">Facebook Live</option>
                <option value="rtmp">Custom RTMP</option>
              </select>
            </label>
            <label>Play{' '}
              <select value={draft.mode ?? 'loop'} onChange={(e) => setEditing({ ...draft, mode: e.target.value as ChannelInput['mode'] })}>
                <option value="loop">on a loop — a crowd can arrive any time</option>
                <option value="once">once — a webinar, then off</option>
              </select>
            </label>
          </div>
          {(draft.platform ?? 'youtube') === 'rtmp' && (
            <label>Ingest URL <input value={draft.ingestUrl ?? ''} onChange={(e) => setEditing({ ...draft, ingestUrl: e.target.value })} placeholder="rtmps://…/live" /></label>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            <div className="sub-dim">The rig — burned onto the videos once when the channel starts, so the loop reads as live on the tenth pass.</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <label>Badge <input value={draft.rig?.badge ?? ''} maxLength={24} style={{ width: 110 }} placeholder="LIVE"
                onChange={(e) => setEditing({ ...draft, rig: { ...(draft.rig ?? {}), badge: e.target.value } })} /></label>
              <label>Colour <input type="color" value={draft.rig?.accent ?? '#dc2626'}
                onChange={(e) => setEditing({ ...draft, rig: { ...(draft.rig ?? {}), accent: e.target.value } })} /></label>
              <label><input type="checkbox" checked={(draft.rig?.mark ?? 'brand') === 'brand'}
                onChange={(e) => setEditing({ ...draft, rig: { ...(draft.rig ?? {}), mark: e.target.checked ? 'brand' : 'none' } })} /> the show's mark, top right</label>
            </div>
            <label>Ticker <input value={draft.rig?.ticker ?? ''} maxLength={300} placeholder="Ask your questions in the chat · New episodes every Tuesday"
              onChange={(e) => setEditing({ ...draft, rig: { ...(draft.rig ?? {}), ticker: e.target.value } })} /></label>
            <label>A look from the Lab <select value={draft.rig?.look ?? ''} onChange={(e) => setEditing({ ...draft, rig: { ...(draft.rig ?? {}), look: e.target.value || null } })} data-tool="channel-look">
              <option value="">none</option>{looks.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.kind}</option>)}</select>
              <span className="sub-dim"> — its overlay is baked under the badge and the mark; make the look first</span></label>
          </div>
          <div>
            <div className="sub-dim">Videos, in order. Only finished cuts can go live.</div>
            {finished.length === 0 && <div className="empty">No finished videos yet — render one on the Flight Deck first.</div>}
            {finished.map((c) => {
              const on = draft.cardIds?.includes(c.id) ?? false;
              return (
                <label key={c.id} style={{ display: 'block' }}>
                  <input type="checkbox" checked={on} onChange={(e) => {
                    const next = (draft.cardIds ?? []).filter((x) => x !== c.id);
                    setEditing({ ...draft, cardIds: e.target.checked ? [...next, c.id] : next });
                  }} /> {c.title}{!c.master_job_id && <span className="sub-dim"> — not rendered</span>}
                </label>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" disabled={busy} onClick={() => void save()}>{draft.id ? 'Save' : 'Create channel'}</button>
            <button className="btn" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {channels.length === 0 && !draft && <div className="empty">No channels yet. A channel is one or more finished videos pushed to a live ingest.</div>}
      {channels.map((ch) => (
        <div key={ch.id} className="r" style={{ display: 'grid', gap: 6, padding: '10px 0', borderTop: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="chip" style={{ background: TONE[ch.status], color: 'white' }}>{ch.status === 'live' ? '● LIVE' : ch.status}</span>
            <strong>{ch.name}</strong>
            <span className="sub-dim">{PLATFORM_LABEL[ch.platform]} · {ch.mode === 'loop' ? 'looped' : 'once'} · {ch.card_ids.length} video{ch.card_ids.length === 1 ? '' : 's'}{ch.rig?.badge ? ` · ${ch.rig.badge} badge` : ''}{ch.rig?.ticker ? ' · ticker' : ''}{ch.status === 'live' ? ` · ${hms(Number(ch.pushed_seconds))} pushed${ch.last_pushed_at ? `, seen ${ago(ch.last_pushed_at)}` : ''}` : ''}</span>
            <span style={{ flex: 1 }} />
            {(ch.status === 'off' || ch.status === 'failed') && (
              <>
                <button className="btn tiny" disabled={busy} onClick={() => setEditing({ id: ch.id, name: ch.name, cardIds: ch.card_ids, mode: ch.mode, platform: ch.platform, ingestUrl: ch.platform === 'rtmp' ? ch.ingest_url : undefined, rig: (ch.rig ?? {}) as ChannelRig })}>Edit</button>
                <button className="btn tiny" disabled={busy} onClick={() => { setKeyFor(keyFor === ch.id ? null : ch.id); setStreamKey(''); }}>Go live…</button>
              </>
            )}
            {(ch.status === 'starting' || ch.status === 'live') && (
              <button className="btn tiny" disabled={busy} onClick={() => void stop(ch.id)}>Stop</button>
            )}
          </div>
          <div className="sub-dim">{ch.card_ids.map(titleOf).join(' → ')}</div>
          <div className="sub-dim" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>also to:</span>
            {(ch.extra ?? []).length === 0 && <span>nowhere else</span>}
            {(ch.extra ?? []).map((d, n) => (
              <span key={n} className="chip">{PLATFORM_LABEL[d.platform]}
                {(ch.status === 'off' || ch.status === 'failed') && (
                  <button className="btn tiny" style={{ marginLeft: 4 }} disabled={busy} title="Remove this destination"
                          onClick={() => { setBusy(true); api.removeDestination(ch.id, n).then(load).catch((e) => setError((e as Error).message)).finally(() => setBusy(false)); }}>×</button>
                )}
              </span>
            ))}
            {(ch.status === 'off' || ch.status === 'failed') && (ch.extra ?? []).length < 4 && (
              <button className="btn tiny" disabled={busy} onClick={() => { setAlsoFor(alsoFor === ch.id ? null : ch.id); setAlsoKey(''); }}>+ another platform</button>
            )}
          </div>
          {alsoFor === ch.id && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={alsoPlatform} onChange={(e) => setAlsoPlatform(e.target.value as 'youtube' | 'facebook' | 'rtmp')}>
                <option value="youtube">YouTube Live</option><option value="facebook">Facebook Live</option><option value="rtmp">Custom RTMP</option>
              </select>
              {alsoPlatform === 'rtmp' && <input value={alsoIngest} onChange={(e) => setAlsoIngest(e.target.value)} placeholder="rtmps://…/live" />}
              <input type="password" autoComplete="off" value={alsoKey} onChange={(e) => setAlsoKey(e.target.value)} placeholder="that platform's stream key" style={{ minWidth: 240 }} />
              <button className="btn" disabled={busy || alsoKey.trim().length < 8}
                      onClick={() => { setBusy(true); api.addDestination(ch.id, { platform: alsoPlatform, ...(alsoPlatform === 'rtmp' && alsoIngest.trim() ? { ingestUrl: alsoIngest.trim() } : {}), streamKey: alsoKey.trim() }).then(() => { setAlsoFor(null); setAlsoKey(''); return load(); }).catch((e) => setError((e as Error).message)).finally(() => setBusy(false)); }}>
                Add — the same push goes there too
              </button>
              <span className="sub-dim">sent once, kept in a vault</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} className="sub-dim">
            <span title="Starts on its own at this time — needs a stream key from an earlier start">start at</span>
            <input type="datetime-local" value={toLocal(ch.start_at)} disabled={busy}
                   onChange={(e) => void api.scheduleChannel(ch.id, { startAt: fromLocal(e.target.value) }).then(load).catch((err) => setError((err as Error).message))} />
            <span>stop at</span>
            <input type="datetime-local" value={toLocal(ch.stop_at)} disabled={busy}
                   onChange={(e) => void api.scheduleChannel(ch.id, { stopAt: fromLocal(e.target.value) }).then(load).catch((err) => setError((err as Error).message))} />
            {(ch.start_at || ch.stop_at) && <span>· checked every five minutes</span>}
          </div>
          {ch.error && <div className="err">{ch.error}</div>}
          {keyFor === ch.id && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="password" autoComplete="off" value={streamKey} onChange={(e) => setStreamKey(e.target.value)}
                placeholder={`stream key from ${PLATFORM_LABEL[ch.platform]}`} style={{ minWidth: 280 }} aria-label="Stream key" />
              <button className="btn" disabled={busy} onClick={() => void start(ch.id)}>Start pushing</button>
              <span className="sub-dim">sent once, kept in a vault, never shown again</span>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
